/**
 * SEOHead Component
 * Reusable component for managing SEO metadata across all pages
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  generateTitle,
  generateDescription,
  generateCanonical,
  generateOGMetadata,
  generateTwitterMetadata,
  generateRobotsMeta,
  injectStructuredData
} from '../utils/seo';

const SEOHead = ({
  title,
  description,
  canonical,
  image,
  noindex = false,
  nofollow = false,
  schema,
  pathname
}) => {
  const finalTitle = generateTitle(title);
  const finalDescription = generateDescription(description);
  const finalCanonical = generateCanonical(canonical);
  const robotsContent = noindex 
    ? 'noindex, nofollow' 
    : nofollow 
      ? 'index, nofollow' 
      : generateRobotsMeta(pathname);
  
  const ogMetadata = generateOGMetadata(finalTitle, finalDescription, image, finalCanonical);
  const twitterMetadata = generateTwitterMetadata(finalTitle, finalDescription, image);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="robots" content={robotsContent} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={finalCanonical} />
      
      {/* Open Graph / Facebook */}
      {Object.entries(ogMetadata).map(([property, content]) => (
        <meta key={property} property={property} content={content} />
      ))}
      
      {/* Twitter */}
      {Object.entries(twitterMetadata).map(([name, content]) => (
        <meta key={name} name={name} content={content} />
      ))}
      
      {/* Structured Data */}
      {schema && (Array.isArray(schema) ? schema.map((s, i) => injectStructuredData(s, i)) : injectStructuredData(schema))}
    </Helmet>
  );
};

export default SEOHead;